import { useEffect, useState } from 'react';
import type { BuilderBlock, BuilderState, Selection, Template } from './types';
import { buildInvoice } from './template';
import { useForm } from 'react-final-form';
import { useFieldValue } from 'hooks';
import { LinkerContext } from '../types';
import { resolveClientPONumber } from './poNumber';

function toggleSelection(selection: Selection, blockId: string, month: string): Selection {
  const { [blockId]: selectedBlock = [], ...rest } = selection;
  const months = selectedBlock.includes(month)
    ? selectedBlock.filter((selected) => selected !== month)
    : [...selectedBlock, month];
  return months.length > 0 ? { ...selection, [blockId]: months } : rest;
}

const createSelection = (blocks: BuilderBlock[]): Selection =>
  blocks.reduce<Selection>((acc, block) => {
    const months = Object.keys(block.months).filter((m) => Object.keys(block.months[m].amounts).length > 0);
    if (months.length > 0) {
      acc[block.id] = months;
    }
    return acc;
  }, {});

export function useBuilderState(linkerContext: LinkerContext, blocks: BuilderBlock[]): BuilderState {
  const form = useForm();
  const useResolvedPO = useFieldValue<boolean>('_useResolvedPO');

  const [selection, setSelection] = useState<Selection>(() => createSelection(blocks));
  const [template, setTemplate] = useState<Template>({
    facet: 'BLOCK',
    feeMode: 'INCLUDED',
    appendComment: false,
    altActivityName: false,
    transparentUnits: false,
    appendPONumbers: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: blocks are derived from context
  useEffect(() => {
    // when finance period changes we reselect
    setSelection(createSelection(blocks));
  }, [linkerContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: blocks are derived from linkerContext
  useEffect(() => {
    const { bound, rows, date_from, date_to } = buildInvoice(
      linkerContext,
      blocks,
      selection,
      template,
      form.getFieldState('rows')?.value || [],
    );

    const timer = setTimeout(() => {
      form.batch(() => {
        form.change('bound', bound);
        form.change('rows', rows);
        form.change('date_from', date_from);
        form.change('date_to', date_to);
        form.change('_template', template);
        if (useResolvedPO) {
          form.change('client_po_number', resolveClientPONumber(linkerContext, bound));
        }
      });
    });
    return () => clearTimeout(timer);
  }, [selection, template, linkerContext, useResolvedPO, form]);

  function select(blockId: string, month: string): void {
    setSelection((current) => toggleSelection(current, blockId, month));
  }

  const clear = Object.keys(selection).length > 0 ? () => setSelection({}) : undefined;

  return {
    selection,
    template,
    select,
    setTemplate,
    clear,
  };
}
